import type { Column, Row } from "@tanstack/react-table";
import type { CSSProperties } from "react";
import { cn } from "@volca/ui/lib/utils";
import type { SimpleTableRowVariant } from "./types";

export const ROW_CLASSES = "border-b-0";
export const HEAD_CLASSES = "whitespace-normal";
export const cellClasses = (noBorder?: boolean) =>
  cn("whitespace-normal", { "border-b": !noBorder });

export const getColumnWidth = <TData>(column: Column<TData>) =>
  column.columnDef?.meta?.size ??
  (column.columnDef?.size === undefined
    ? undefined
    : `${column.columnDef.size}px`);

export const isFixedSize = <TData>(column: Column<TData>) =>
  column.columnDef?.minSize !== undefined &&
  column.columnDef.minSize === column.columnDef.maxSize;

export const alignClasses = <TData>(column: Column<TData>) =>
  cn({ "text-right tabular-nums": column.columnDef?.meta?.align === "right" });

const PINNED_LEFT_EDGE =
  "border-r border-r-transparent in-data-scrolled-left:border-r-line";
const PINNED_RIGHT_EDGE =
  "border-l border-l-transparent in-data-scrolled-right:border-l-line";

export const getPinnedEdgeClassName = <TData>(column: Column<TData>) => {
  const isPinned = column.getIsPinned();
  if (isPinned === "left" && column.getIsLastColumn("left"))
    return PINNED_LEFT_EDGE;
  if (isPinned === "right" && column.getIsFirstColumn("right"))
    return PINNED_RIGHT_EDGE;
  return "";
};

export const getPinningStyles = <TData>(
  column: Column<TData>,
  isHeader = false,
): CSSProperties => {
  const isPinned = column.getIsPinned();
  const isLastLeftPinnedColumn =
    isPinned === "left" && column.getIsLastColumn("left");
  const isFirstRightPinnedColumn =
    isPinned === "right" && column.getIsFirstColumn("right");

  return {
    position: isPinned ? "sticky" : "relative",
    zIndex: isPinned ? (isHeader ? 11 : 2) : 0,
    left: isPinned === "left" ? `${column.getStart("left")}px` : undefined,
    right: isPinned === "right" ? `${column.getAfter("right")}px` : undefined,
    paddingRight:
      isPinned === "left" && !isLastLeftPinnedColumn ? 0 : undefined,
    paddingLeft:
      isPinned === "right" && !isFirstRightPinnedColumn ? 0 : undefined,
    width: isPinned ? (getColumnWidth(column) ?? "auto") : "auto",
  };
};

export const ROW_VARIANT_ROW_CLASSES: Record<SimpleTableRowVariant, string> = {
  warning:
    "bg-warning-subtle hover:bg-warning-hovered data-[state=selected]:bg-warning-hovered",
  dismissed: "bg-danger-subtle hover:bg-danger-hovered",
  destructive: "bg-danger-subtle hover:bg-danger-hovered",
};

const ROW_VARIANT_PINNED_HOVER_CLASSES: Record<
  SimpleTableRowVariant | "default",
  string
> = {
  default:
    "group-hover:bg-[color-mix(in_oklab,var(--ink)_8%,var(--surface-raised))]! group-focus-visible:bg-[color-mix(in_oklab,var(--ink)_8%,var(--surface-raised))]! group-data-[state=selected]:bg-selected!",
  warning:
    "group-hover:bg-warning-hovered! group-data-[state=selected]:bg-warning-hovered!",
  dismissed:
    "group-hover:bg-danger-hovered! group-data-[state=selected]:bg-selected!",
  destructive:
    "group-hover:bg-danger-hovered! group-data-[state=selected]:bg-danger-hovered!",
};

export const getPinnedCellClassName = <TData>(
  column: Column<TData>,
  isStriped: boolean,
  isLastRow: boolean,
  rowVariant: SimpleTableRowVariant | "default" = "default",
): string => {
  const isPinned = column.getIsPinned();
  if (!isPinned) return "";
  const baseClass =
    rowVariant !== "default"
      ? ROW_VARIANT_ROW_CLASSES[rowVariant]
      : isStriped
        ? "bg-stripe"
        : "bg-surface-raised";
  const borderClass = isLastRow
    ? "border-b-0"
    : "border-b-0 shadow-[inset_0_-1px_0_0_var(--line)]";
  const hoverClass = ROW_VARIANT_PINNED_HOVER_CLASSES[rowVariant];
  return cn(baseClass, borderClass, "transition-colors", hoverClass);
};

export const getRowStripeIndex = <TData>(
  row: Row<TData>,
  allRows: Row<TData>[],
): number => {
  if (row.depth > 0 && row.parentId) {
    const parent = allRows.find((r) => r.id === row.parentId);
    if (parent) return parent.index;
  }
  return row.index;
};
